/**
 * <LineItemCategoriesCard /> — donut + legend showing inferred-category
 * breakdown of line items for the chosen period. Drops on the dashboard
 * just under the financial breakdown.
 *
 * Renders nothing when the user has no line items yet — we don't want to
 * tease an empty chart.
 */
import { useMemo, useState } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { Card } from "../ui/card";
import { Tag, Lightbulb } from "lucide-react";
import { formatMoney } from "../../lib/api";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { useLineItemCategories, useCategoryLabels, useProfile } from "../../hooks/queries";

// Tailwind-derived palette; deterministic per index so the colour for
// "frutas" is the same across renders even when ordering shifts.
const COLORS = [
  "#16A34A", "#0EA5E9", "#F59E0B", "#EF4444", "#8B5CF6", "#14B8A6", "#EC4899",
  "#A855F7", "#3B82F6", "#22C55E", "#F97316", "#06B6D4", "#84CC16", "#E11D48",
  "#6366F1", "#10B981", "#FB923C", "#0891B2",
];

const PERIODS = [
  { v: "month",   es: "Este mes",   en: "This month",  pt: "Este mês" },
  { v: "quarter", es: "Últimos 90d",en: "Last 90d",    pt: "Últimos 90d" },
  { v: "ytd",     es: "Año a la fecha", en: "Year to date", pt: "Ano até hoje" },
  { v: "all",     es: "Todo el tiempo",  en: "All time", pt: "Todo o tempo" },
];


function PieTooltip({ active, payload, currency, total }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const pct = total > 0 ? Math.round((d.total / total) * 100) : 0;
  return (
    <div className="rounded-lg bg-white shadow-lg border border-slate-200 px-3 py-2 text-xs">
      <div className="font-semibold text-slate-900 mb-1 flex items-center gap-1.5">
        <span
          className="inline-block h-2.5 w-2.5 rounded-sm"
          style={{ backgroundColor: d.fill }}
        />
        {d.label}
      </div>
      <div className="tabular-nums text-slate-700">
        {formatMoney(d.total, currency)} <span className="text-slate-400">·</span> <span className="font-medium">{pct}%</span>
      </div>
      <div className="text-[10px] text-slate-500 mt-0.5">
        {d.item_count} {d.item_count === 1 ? "ítem" : "ítems"}
      </div>
    </div>
  );
}


export function LineItemCategoriesCard() {
  const { lang } = useLang();
  const { data: profile } = useProfile();
  const currency = profile?.currency || "USD";
  const [period, setPeriod] = useState("month");
  const { data, isLoading } = useLineItemCategories(period);
  const { data: labelsMap } = useCategoryLabels();

  const series = useMemo(() => {
    const cats = data?.categories || [];
    return cats.map((c, idx) => ({
      ...c,
      label: labelsMap?.[c.category]?.[lang] || labelsMap?.[c.category]?.es || c.category,
      fill: COLORS[idx % COLORS.length],
    }));
  }, [data?.categories, labelsMap, lang]);

  const total = data?.total || 0;
  // Hide the card completely when there's no signal — no UX value in a
  // 0/0 donut. The dashboard already greets new users elsewhere.
  if (!isLoading && total <= 0) return null;

  const top = series[0];

  return (
    <Card className="p-5 rounded-2xl border-slate-200" data-testid="line-item-categories-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Lightbulb className="h-4 w-4 text-amber-500" />
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              {tx(lang, "Top categories", "Top categorías", "Top categorias")}
            </span>
          </div>
          <h3 className="font-display text-lg font-bold text-slate-900">
            {tx(lang,
              "Inferred breakdown of your receipts",
              "Detalle inferido de tus tickets",
              "Detalhe inferido dos seus recibos",
            )}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {tx(lang,
              "Computed from the items inside your movements.",
              "Calculado desde los ítems registrados en tus movimientos.",
              "Calculado a partir dos itens registrados nos seus movimentos.",
            )}
          </p>
        </div>
        <select /* design-system: ALLOW legacy-todo */
          data-testid="categories-period-select"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="text-xs rounded-md border border-slate-200 px-2 py-1 bg-white shrink-0"
        >
          {PERIODS.map((p) => (
            <option key={p.v} value={p.v}>{p[lang] || p.en}</option>
          ))}
        </select>
      </div>

      <div className="grid sm:grid-cols-[180px_1fr] gap-5 items-center">
        <div className="h-[180px] mx-auto" style={{ width: 180 }}>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={series}
                dataKey="total"
                nameKey="label"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={1}
                isAnimationActive={false}
              >
                {series.map((s) => (
                  <Cell key={s.category} fill={s.fill} stroke="white" strokeWidth={1.5} />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip currency={currency} total={total} />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="min-w-0">
          {top && (
            <div className="mb-3 pb-3 border-b border-slate-100">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                {tx(lang, "Top category", "Categoría #1", "Categoria #1")}
              </div>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ backgroundColor: top.fill }}
                />
                <span className="font-bold text-slate-900 truncate" data-testid="categories-top-label">
                  {top.label}
                </span>
                <span className="tabular-nums font-display text-emerald-700 font-bold">
                  {formatMoney(top.total, currency)}
                </span>
                {total > 0 && (
                  <span className="text-[10px] text-slate-500 tabular-nums">
                    {Math.round((top.total / total) * 100)}%
                  </span>
                )}
              </div>
            </div>
          )}
          <div className="grid gap-1.5" data-testid="categories-legend">
            {series.slice(0, 8).map((s) => {
              const pct = total > 0 ? Math.round((s.total / total) * 100) : 0;
              return (
                <div
                  key={s.category}
                  data-testid={`category-row-${s.category}`}
                  className="grid grid-cols-[10px_1fr_auto_auto] items-center gap-2 text-xs"
                >
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ backgroundColor: s.fill }}
                  />
                  <span className="text-slate-700 truncate" title={s.items.join(", ")}>
                    {s.label}
                  </span>
                  <span className="tabular-nums text-slate-500">{pct}%</span>
                  <span className="tabular-nums font-semibold text-slate-900">
                    {formatMoney(s.total, currency)}
                  </span>
                </div>
              );
            })}
            {series.length > 8 && (
              <div className="text-[10px] text-slate-400 mt-1">
                + {series.length - 8} {tx(lang, "more", "más", "mais")}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-3 text-[10px] text-slate-400">
            <Tag className="h-3 w-3" />
            {tx(lang,
              `${data?.item_count || 0} items · ${formatMoney(total, currency)} total`,
              `${data?.item_count || 0} ítems · ${formatMoney(total, currency)} total`,
              `${data?.item_count || 0} itens · ${formatMoney(total, currency)} total`,
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
